import useThemes from 'hooks/useThemes';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector, useAppTranslation, useErrTranslation } from 'app/hooks';
import { platformStates } from 'redux/platformSlice';
import Typo from 'consts/typography';
import { isValidAmount } from './WithdrawModal';
import { CoinPairDto } from 'models/wallet';
import walletApi from 'api/walletApi';
import transactionApi from 'api/transactionApi';
import { showConfirmModal, showErrorModal, showSuccessModal } from 'components/Modals/CommonModals';
import { useHandleGetWallets } from 'hooks/useHandleGetWallets';
import { IMG_ALT } from 'consts/app';
import ArrowIcon from 'assets/icons/ArrowIcon';
import { Select } from 'antd';
import CustomAmountButton from 'components/Commons/CustomAmountButton';
import InfomationIcon from 'assets/icons/InfomationIcon';
import { formatAmountV2 } from 'utils/formatAmount';

interface TbetBuySellModalProps {}

const TbetBuySellModal: React.FunctionComponent<TbetBuySellModalProps> = (props) => {
  const { currentTheme } = useThemes();
  const [amount, setAmount] = useState<string>('');
  const [isReverse, setIsReverse] = useState<boolean>(true);
  const { isPhone, isTablet } = useAppSelector(platformStates);
  const t = useAppTranslation();
  const et = useErrTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [coinPair, setCoinPair] = useState<CoinPairDto | null>(null);
  const [coinPairs, setCoinPairs] = useState<CoinPairDto[] | null>(null);
  const [tbetPrice, setTbetPrice] = useState<number>(0);
  const { userWallets } = useHandleGetWallets();
  const [errorMessage, setErrorMessage] = useState('');

  const getCoinPairs = async () => {
    const { ok, body } = await walletApi.getCoinPair();
    if (ok && body) {
      setCoinPair(body[0]);
      setCoinPairs(body);
    }
  };

  const getPrice = async () => {
    const { ok, body } = await walletApi.getTokenPrice('$TBET');
    if (ok && body) {
      setTbetPrice(body.price);
    }
  };

  const rate = useMemo(() => {
    if (!isReverse) {
      return tbetPrice;
    }
    return 1 / tbetPrice;
  }, [tbetPrice, isReverse]);

  useEffect(() => {
    getCoinPairs();
    return () => resetModal();
  }, []);

  useEffect(() => {
    getPrice();
  }, []);

  const resetModal = () => {
    setAmount('');
    setIsReverse(true);
  };

  const currentCoinPair = useMemo(() => {
    return isReverse
      ? coinPairs?.find((c) => c.quote.symbol === 'USD' && c.base.symbol === '$TBET')
      : coinPairs?.find((c) => c.quote.symbol === '$TBET' && c.base.symbol === 'USD');
  }, [coinPairs, isReverse]);

  const getFeeSwap = useMemo(() => {
    if (currentCoinPair?.isFixed === true) {
      return currentCoinPair.fixedFee;
    }
    if (currentCoinPair?.isPercentage === true) {
      return (Number(amount) * rate * currentCoinPair.percentageFee) / 100;
    } else {
      return 0;
    }
  }, [coinPairs, amount, currentCoinPair]);

  const receiveAmount = useMemo(() => {
    if (rate) {
      if (amount === '') {
        return 0;
      }
      return Number(amount) * rate - getFeeSwap;
    }
    return 0;
  }, [amount, isReverse, rate, getFeeSwap]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^$|^(1000000(\.0{0,2})?|[0-9]{1,6}(\.[0-9]{0,2})?|0\.)$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setAmount(value);
    }
  };

  const onSubmit = useCallback(async () => {
    if (currentCoinPair?.id) {
      setIsLoading(true);
      const { ok, body, error } = await transactionApi.orderSwapToken({
        amount: Number(amount),
        coinPairId: currentCoinPair?.id,
      });

      if (ok && body) {
        const content = isReverse ? `${receiveAmount} $TBET` : `${receiveAmount} USD`;
        showSuccessModal({
          title: t('tbet.buy.success-title'),
          content: t('tbet.buy.success-message', { amount: content }),
        });
      }
      if (error) {
        showErrorModal({
          content: et(error.message),
          onOk: () => {},
          title: et(error.title || '', { defaultValue: et('defaultMessage.title') }),
        });
      }
      setIsLoading(false);
    }
  }, [amount, currentCoinPair]);

  const selectionCoins = useMemo(() => {
    if (coinPair) {
      const coinsArray = [coinPair.base, coinPair.quote];
      return coinsArray.map((coin) => ({
        value: coin.symbol === 'USD' ? true : false,
        label: (
          <div className="flex-row items-center gap-2 h-full">
            <img src={coin.image} alt={IMG_ALT} className="dropdown-logo" />
            <span
              className={`primary-text-color ${isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2}`}
            >
              {coin.symbol}
            </span>
          </div>
        ),
      }));
    }
  }, [coinPair?.id]);

  const onCoinChange = (event: boolean) => {
    setIsReverse(event);
  };

  const usdBalance = useMemo(
    () => userWallets.find((w) => w.coin.symbol === 'USD')?.balance,
    [userWallets],
  );

  const tbetBalance = useMemo(
    () => userWallets.find((w) => w.coin.symbol === '$TBET')?.balance,
    [userWallets],
  );

  const handleSetField = (value: string) => {
    setAmount(value);
  };
  const handleDoubleClickOption = useCallback(
    (value: string) => {
      showConfirmModal({
        content: (
          <p>
            {t('confirmModal.tbet.swap.message', {
              amount: `${value} ${isReverse ? 'USD' : '$TBET'}`,
            })}
          </p>
        ),
        width: 360,
        icon: <InfomationIcon />,
        onOk: onSubmit,
        okText: 'Submit',
        cancelText: 'Cancel',
      });
    },
    [amount],
  );

  useEffect(() => {
    if (amount && Number(formatAmountV2(receiveAmount, 8)) <= 0) {
      setErrorMessage(t('tbet.swap.error.receiveAmount'));
    } else {
      setErrorMessage('');
    }
  }, [receiveAmount, amount]);

  return (
    <>
      {coinPair && (
        <div className={`flex w-100 items-center swap-token__container `}>
          <div className="swap-token__coin ">
            <span className={`primary-text-color ${Typo.p5} `}>{t('tbet.buy.from')}</span>
            <div>
              <Select
                suffixIcon={null}
                className="antd__select w-full"
                dropdownClassName="antd__select-dropdown"
                value={isReverse}
                onChange={onCoinChange}
                disabled={true}
                options={selectionCoins}
              />
            </div>
            <span className={`primary-text-color ${isPhone ? Typo.p8 : Typo.p5} `}>
              {t('balance')}:{' '}
              {isReverse ? formatAmountV2(usdBalance, 2) : formatAmountV2(tbetBalance, 8)}
            </span>
          </div>
          <ArrowIcon
            fill={currentTheme === 'dark' ? '#FFFFFF' : '#000000'}
            width="40px"
            height="40px"
          />
          <div className="swap-token__coin">
            <span className={`primary-text-color ${Typo.p5} `}>{t('tbet.buy.to')}</span>
            <div>
              <Select
                suffixIcon={null}
                className="antd__select w-full"
                dropdownClassName="antd__select-dropdown"
                value={!isReverse}
                onChange={onCoinChange}
                disabled={true}
                options={selectionCoins}
              />
            </div>
            <span className={`primary-text-color ${isPhone ? Typo.p8 : Typo.p5} `}>
              {t('balance')}:{' '}
              {!isReverse ? formatAmountV2(usdBalance, 2) : formatAmountV2(tbetBalance, 8)}
            </span>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-3 items-center">
        <Fragment>
          <div className="common-input input-amount w-100 swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            >
              {t('cashier.amount')}
            </label>
            <input
              type="text"
              name="amount"
              id="amount"
              autoComplete="off"
              onChange={onChange}
              value={amount}
              placeholder={t('placeholder.enterAmount')}
            />
          </div>
          <div className="common-input input-amount w-100 swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            ></label>
            <CustomAmountButton
              symbol={isReverse ? 'USD' : '$TBET'}
              onSetField={handleSetField}
              onOptionDoubleClick={handleDoubleClickOption}
              classes="custom-button-swap"
            />
          </div>
          <div className="common-input input-amount w-100 swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            >
              {t('tbet.buy.rate')}
            </label>
            <input
              type="text"
              name="amount"
              id="amount"
              autoComplete="off"
              disabled={true}
              value={rate}
            />
          </div>
          <div className="common-input input-amount w-100 swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="feeswap"
            >
              {t('tbet.buy.feeSwap')}
            </label>
            <input
              type="text"
              name="feeswap"
              id="feeswap"
              autoComplete="off"
              disabled={true}
              value={formatAmountV2(getFeeSwap, 8)}
            />
          </div>
          <div className="common-input input-amount w-100 swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
              htmlFor="amount"
            >
              {t('tbet.buy.receiveAmount')}
            </label>
            <input
              type="text"
              name="amount"
              id="amount"
              autoComplete="off"
              disabled={true}
              value={formatAmountV2(receiveAmount, 8)}
            />
          </div>
          <div className="common-input input-amount w-100 swap-input">
            <label
              className={`${
                isPhone ? Typo.p6 : isTablet ? Typo.p4 : Typo.p2
              } font-medium primary-text-color`}
            ></label>
            <span className="error-message">{errorMessage}</span>
          </div>
        </Fragment>
        <div className="withdraw-balance swap-input">
          <div className="withdraw-balance__left"></div>
          <div className="withdraw-balance__right">
            <button
              disabled={isLoading || Number(amount) === 0 || receiveAmount <= 0}
              className={`button-generate ${
                isLoading || Number(amount) === 0 || receiveAmount <= 0 ? 'disabled' : ''
              }`}
              type="submit"
              onClick={onSubmit}
            >
              {t('tbet.buy.button')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TbetBuySellModal;
